import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";

/**
 * Global notification settings data model
 */
export const NotificationSettings = {
  id: "",
  ownerId: "",
  // Default rent reminder settings
  defaultRentReminders: [
    { daysBeforeDue: 7, enabled: true, channel: "email" },
    { daysBeforeDue: 1, enabled: true, channel: "email" },
  ],
  // Default late rent notification settings
  defaultLateNotifications: [
    { daysAfterDue: 1, enabled: true, channel: "email" },
  ],
  // Email settings
  emailSettings: {
    fromEmail: "noreply@landlordops.com",
    fromName: "LandlordOps",
    replyToEmail: "",
    smtpSettings: {
      host: "",
      port: 587,
      secure: false,
      username: "",
      password: "",
    },
  },
  // SMS settings (for future use)
  smsSettings: {
    provider: "twilio", // 'twilio', 'aws_sns', etc.
    accountSid: "",
    authToken: "",
    fromNumber: "",
  },
  // Late fee settings
  lateFeeSettings: {
    amount: 50.0,
    gracePeriod: 3,
    accruesDaily: false,
    dailyAmount: 10.0,
  },
  createdAt: null,
  updatedAt: null,
};

/**
 * Get or create notification settings for the current user
 */
export async function getOrCreateNotificationSettings() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "notificationSettings"),
      where("ownerId", "==", user.uid)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Create default settings
      const defaultSettings = {
        ownerId: user.uid,
        defaultRentReminders: [
          { daysBeforeDue: 7, enabled: true, channel: "email" },
          { daysBeforeDue: 1, enabled: true, channel: "email" },
        ],
        defaultLateNotifications: [
          { daysAfterDue: 1, enabled: true, channel: "email" },
        ],
        emailSettings: {
          fromEmail: "noreply@landlordops.com",
          fromName: "LandlordOps",
          replyToEmail: "",
          smtpSettings: {
            host: "",
            port: 587,
            secure: false,
            username: "",
            password: "",
          },
        },
        smsSettings: {
          provider: "twilio",
          accountSid: "",
          authToken: "",
          fromNumber: "",
        },
        lateFeeSettings: {
          amount: 50.0,
          gracePeriod: 3,
          accruesDaily: false,
          dailyAmount: 10.0,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        collection(db, "notificationSettings"),
        defaultSettings
      );
      return { id: docRef.id, ...defaultSettings };
    }

    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error("Error getting notification settings:", error);
    throw error;
  }
}

/**
 * Update notification settings
 */
export async function updateNotificationSettings(updates) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "notificationSettings"),
      where("ownerId", "==", user.uid)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("Notification settings not found");
    }

    const doc = querySnapshot.docs[0];
    const docRef = doc(db, "notificationSettings", doc.id);

    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
      message: "Notification settings updated successfully",
    };
  } catch (error) {
    console.error("Error updating notification settings:", error);
    throw error;
  }
}

/**
 * Update late fee settings
 */
export async function updateLateFeeSettings(lateFeeSettings) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "notificationSettings"),
      where("ownerId", "==", user.uid)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("Notification settings not found");
    }

    const doc = querySnapshot.docs[0];
    const docRef = doc(db, "notificationSettings", doc.id);

    await updateDoc(docRef, {
      lateFeeSettings: lateFeeSettings,
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
      message: "Late fee settings updated successfully",
    };
  } catch (error) {
    console.error("Error updating late fee settings:", error);
    throw error;
  }
}

/**
 * Update email settings
 */
export async function updateEmailSettings(emailSettings) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "notificationSettings"),
      where("ownerId", "==", user.uid)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("Notification settings not found");
    }

    const doc = querySnapshot.docs[0];
    const docRef = doc(db, "notificationSettings", doc.id);

    await updateDoc(docRef, {
      emailSettings: emailSettings,
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
      message: "Email settings updated successfully",
    };
  } catch (error) {
    console.error("Error updating email settings:", error);
    throw error;
  }
}

/**
 * Test email configuration
 */
export async function testEmailConfiguration() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // TODO: Implement actual email test
    // This would send a test email to verify the configuration

    return {
      success: true,
      message: "Email configuration test completed successfully",
    };
  } catch (error) {
    console.error("Error testing email configuration:", error);
    throw error;
  }
}
